// PeptideVault v6.5 — Core utilities, date helpers, theme

// ══════════════════════════════════════════════════════
// CONSTANTS & HELPERS
// ══════════════════════════════════════════════════════
const uid = () => Math.random().toString(36).slice(2, 10)
const esc = s => String(s||'').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;')
const now = () => new Date().toISOString()
const fmt = d => d ? new Date(d).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) : '—'
const fmtCur = n => n ? `$${parseFloat(n).toFixed(2)}` : '—'
const sleep = ms => new Promise(r => setTimeout(r, ms))
// Detect Electron via user-agent (works even when preload.js isn't loaded yet)
const APP_VERSION = typeof process !== 'undefined' && process.env.APP_VERSION || '9.1.1'

// One ordering rule for static forms, modals, and options appended later.
// Empty or disabled prompt options remain pinned above the A-Z choices.
const dropdownCollator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' })
let dropdownSortObserver = null
let dropdownSortQueued = false

function dropdownOptionLabel(option) {
  return String(option && (option.label || option.textContent) || '').trim()
}

function sortSelectOptionsAlphabetically(select) {
  if (!select || select.tagName !== 'SELECT') return false
  const selectedValues = Array.from(select.selectedOptions || []).map(option => option.value)
  const parents = [select].concat(Array.from(select.querySelectorAll(':scope > optgroup')))
  let changed = false

  parents.forEach(parent => {
    const options = Array.from(parent.children).filter(child => child.tagName === 'OPTION')
    if (options.length < 2) return
    const pinned = options.filter(option => option.value === '' || option.disabled || option.dataset.sortPin === 'true')
    const sortable = options.filter(option => !pinned.includes(option)).sort((left, right) =>
      dropdownCollator.compare(dropdownOptionLabel(left), dropdownOptionLabel(right))
    )
    const desired = pinned.concat(sortable)
    if (desired.every((option, index) => options[index] === option)) return
    desired.forEach(option => parent.appendChild(option))
    changed = true
  })

  if (select.multiple) {
    Array.from(select.options).forEach(option => { option.selected = selectedValues.includes(option.value) })
  } else if (selectedValues.length) {
    select.value = selectedValues[0]
  }
  return changed
}

function sortAllDropdowns(root) {
  const scope = root || document
  if (scope.matches && scope.matches('select')) sortSelectOptionsAlphabetically(scope)
  if (scope.querySelectorAll) scope.querySelectorAll('select').forEach(sortSelectOptionsAlphabetically)
}

function installAlphabeticalDropdownSorting() {
  if (typeof document === 'undefined') return
  sortAllDropdowns(document)
  if (dropdownSortObserver || typeof MutationObserver === 'undefined') return
  dropdownSortObserver = new MutationObserver(function() {
    if (dropdownSortQueued) return
    dropdownSortQueued = true
    queueMicrotask(function() {
      dropdownSortQueued = false
      sortAllDropdowns(document)
    })
  })
  dropdownSortObserver.observe(document.documentElement, { childList: true, subtree: true })
}

// ══════════════════════════════════════════════════════
// DARK MODE THEME MANAGEMENT
// ══════════════════════════════════════════════════════
const THEME_KEY = 'pv-theme'
function getTheme() { return localStorage.getItem(THEME_KEY) || 'light' }
function setTheme(mode) { localStorage.setItem(THEME_KEY, mode); applyTheme(mode) }
function applyTheme(mode) {
  const root = document.documentElement
  if (mode === 'dark') root.setAttribute('data-theme', 'dark')
  else if (mode === 'light') root.setAttribute('data-theme', 'light')
  else root.removeAttribute('data-theme')
}
function cycleTheme() {
  const next = isDarkMode() ? 'light' : 'dark'
  setTheme(next)
  showToast(next === 'dark' ? 'Midnight Violet' : 'Frosted Blue')
  render()
}
function isDarkMode() {
  const t = getTheme()
  if (t === 'dark') return true
  if (t === 'light') return false
  return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
}
applyTheme(getTheme())
if (window.matchMedia) window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', function() { if (getTheme() === 'system') applyTheme('system') })

const THEME_ICONS = {
  system: '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><rect x="3" y="3" width="14" height="10" rx="2"/><line x1="7" y1="16" x2="13" y2="16"/><line x1="10" y1="13" x2="10" y2="16"/></svg>',
  light: '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><circle cx="10" cy="10" r="4"/><path d="M10 2v2M10 16v2M2 10h2M16 10h2M4.93 4.93l1.41 1.41M13.66 13.66l1.41 1.41M4.93 15.07l1.41-1.41M13.66 6.34l1.41-1.41"/></svg>',
  dark: '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z"/></svg>'
}

// ══════════════════════════════════════════════════════
// LOCAL DATE HELPERS — fixes UTC "today" bug
// Using local timezone, not UTC, for calendar-day operations
// ══════════════════════════════════════════════════════
function localDateKey(d) {
  // Returns YYYY-MM-DD in local timezone. No argument = today.
  if (!d) d = new Date()
  var y = d.getFullYear(), m = d.getMonth() + 1, day = d.getDate()
  return y + '-' + (m < 10 ? '0' : '') + m + '-' + (day < 10 ? '0' : '') + day
}
function localDateOffset(days) {
  // Returns YYYY-MM-DD for today ± days in local timezone
  var d = new Date()
  d.setDate(d.getDate() + days)
  return localDateKey(d)
}
function localDateStr(d) {
  // Converts any Date object to YYYY-MM-DD in local timezone
  if (!d || !(d instanceof Date) || isNaN(d)) return ''
  return localDateKey(d)
}

const isElectron = typeof navigator !== 'undefined' &&
  navigator.userAgent.toLowerCase().includes('electron')

// window.api is injected by preload.js via contextBridge.
// If we're in Electron but window.api is missing, preload.js is stale or missing.
function apiReady() {
  if (window.api) return true
  if (isElectron) {
    // We're inside Electron but preload.js didn't inject the API bridge.
    // This almost always means preload.js wasn't updated along with the other files.
    showToast('⚠️ preload.js needs to be updated — see instructions', 6000)
    console.error('PeptideVault: window.api is undefined inside Electron. ' +
      'Copy the updated preload.js from the zip into src/ and restart.')
  }
  return false
}

export {
  uid,
  esc,
  now,
  fmt,
  fmtCur,
  sleep,
  APP_VERSION,
  dropdownOptionLabel,
  sortSelectOptionsAlphabetically,
  sortAllDropdowns,
  installAlphabeticalDropdownSorting,
  THEME_KEY,
  getTheme,
  setTheme,
  applyTheme,
  cycleTheme,
  isDarkMode,
  THEME_ICONS,
  localDateKey,
  localDateOffset,
  localDateStr,
  isElectron,
  apiReady
}
